/**
 * The roll's five instants, as arithmetic (docs/03 §6).
 *
 * Market N anchors at `T`, trades the whole of `[T, T+60]`, and **market N+1
 * opens in the instant N expires** — since register §F24 the strike is observed
 * over `[anchor − 10s, anchor)`, so it is final before the market anchors and
 * there is no untradable interval. What still spans thirteen seconds is N's
 * *resolution*, and it now runs behind a market that is already trading.
 *
 * ```
 *  T          T+42+U(0,8)   T+60          T+73  T+74+U(0,4) T+77±1  T+90+U(0,6)
 *  │               │          │             │         │         │         │
 *  anchor(N)   pre-warm N+1  swap        RESOLVED    drop     watch-     drop
 *                                          (N)     N depth     dog    market.{N}
 * ```
 *
 * **Four of the five carry a per-tab jitter and the swap carries none**, which
 * is the shape rather than an inconsistency: the swap is the one instant that
 * sends no frame at all (see below), so there is nothing to spread. Everything
 * that reaches the network is spread.
 *
 * Everything here is a pure function of the anchor, `config.market`'s own
 * offsets and a per-tab jitter. Nothing reads a clock, nothing touches a store,
 * and `δ` — the lag between the settlement window closing and `RESOLVED` — comes
 * from `config.market.resolve_eta_offset_ms` rather than from a literal 13,000,
 * because invariant 11 is the one rule that made §F24 cost one unit test.
 *
 * **The jitter is drawn once per tab per session, never per roll** (§6.1 item
 * 4). A fresh draw each minute is a fresh chance to land on the boundary, and
 * across 3,000 tabs sixty times an hour that is a herd waiting to happen.
 *
 * ## Why the pre-warm is load-bearing rather than an optimisation
 *
 * `T+60` is the instant the user may trade N+1. A snapshot fetch there is 250 ms
 * of a live market they cannot act on, sixty times an hour — where under the old
 * design the same 250 ms was paid inside a window in which nothing was tradable
 * and was invisible (§6.4).
 *
 * ## Why the swap fires no control frame at all
 *
 * §6.0 Fact 2: all 3,000 tabs hit `T+60` within their clock error of each other,
 * so **nothing may be fetched, subscribed or negotiated at the boundary**. The
 * pre-warm therefore subscribes everything the *next* cycle's desired set will
 * name — both assets' successors and the focused asset's depth — so that at
 * `T+60` the set is unchanged and `setDesired` sends nothing. See
 * `Runtime#desiredChannels`.
 *
 * No React, no DOM, no clock.
 */

import { MARKET_STATES, phaseOf, type MarketState } from './lifecycle'

/** `T+42`. The earliest a tab may subscribe the successor's depth channels. */
export const PREWARM_BASE_MS = 42_000
/** The 8-second window the pre-warm is spread over, per tab (§6.1 item 4). */
export const PREWARM_JITTER_MS = 8_000
/** `resolve_eta_ms + 4000` — how long past the server's own estimate the client
 *  waits before reconciling over HTTP (§6.5). */
export const WATCHDOG_LAG_MS = 4_000
/** `U(0, 2000)ms` on top, so a real backend stall is not a stampede (§6.5). */
export const WATCHDOG_JITTER_MS = 2_000
/** `T+74`: one second past `resolve_eta`, which is where §6.2 puts the batched
 *  `unsubscribe` of the dead market's depth channels. */
export const TEARDOWN_GRACE_MS = 1_000
/** `T+90`. `market.{N}` outlives its depth channels by sixteen seconds because
 *  the receipt is still on screen until `T+81` (§6.2). */
export const RETIRE_AFTER_EXPIRY_MS = 30_000
/**
 * The windows the two teardowns are spread over, per tab.
 *
 * **Both are control frames, and until this was added both fired on the dot.**
 * `TEARDOWN` and `RETIRE` each call `Runtime#applyHolds`, which recomputes the
 * desired set and hands it to `setDesired`, which sends one `unsubscribe` — so
 * an unjittered instant is 3,000 tabs sending an unsubscribe inside one clock
 * error of each other, twice a minute, on a venue that already treats the herd
 * shape as a capacity parameter (`config.ws`'s backoff, §6.1 item 4's pre-warm).
 * The pre-warm and the watchdog were spread for exactly this reason; these two
 * were not, and nothing about them is different.
 *
 * **Forward only, and that is the half that keeps them correct.** Both instants
 * are the end of a hold, so adding to one lengthens it and subtracting would
 * shorten it — a teardown drawn *early* would drop a market's depth channels
 * before `resolve_eta`, which is the one thing `TEARDOWN_GRACE_MS` exists to
 * prevent. A late one costs a dead market's four idle subscriptions for a few
 * more seconds; a dead market publishes nothing, so it costs no frames at all.
 *
 * Sized so `teardown < retire` holds for **every** pair of draws at any
 * `resolve_eta_offset_ms` this venue has published (2,000 since §F26, 13,000
 * before it): the largest teardown is `offset + 1,000 + 4,000` and the smallest
 * retire is 30,000. *keeps the teardown before the retire at every corner of
 * both draws* asserts that rather than leaving it to arithmetic in a comment,
 * because the ordering is what stops `market.{id}` being dropped while its
 * depth channels are still held.
 */
export const TEARDOWN_JITTER_MS = 4_000
/** See `TEARDOWN_JITTER_MS`. Wider, because nothing is measured from it. */
export const RETIRE_JITTER_MS = 6_000

export interface RollTiming {
  periodMs: number
  /** `config.market.resolve_eta_offset_ms`. `δ`, and it is not a constant. */
  resolveEtaOffsetMs: number
}

/** Drawn once per tab per session. Never re-drawn per roll. */
export interface RollJitter {
  prewarmMs: number
  watchdogMs: number
  teardownMs: number
  retireMs: number
}

export function drawRollJitter(random: () => number = Math.random): RollJitter {
  return {
    prewarmMs: Math.floor(random() * PREWARM_JITTER_MS),
    watchdogMs: Math.floor(random() * WATCHDOG_JITTER_MS),
    teardownMs: Math.floor(random() * TEARDOWN_JITTER_MS),
    retireMs: Math.floor(random() * RETIRE_JITTER_MS),
  }
}

export type RollEventKind =
  /** Drop the dead market's depth channels and its book, tape, ticker and
   *  candles. `T+74`. */
  | 'TEARDOWN'
  /** The roll's one permitted HTTP call, and only if `RESOLVED` never arrived. */
  | 'WATCHDOG'
  /** Drop `market.{N}` itself. `T+90`. */
  | 'RETIRE'
  /** One batched subscribe for everything the next cycle needs. `T+42+U(0,8)`. */
  | 'PREWARM'
  /** Re-point the page at the successor. `T+60`, and it sends no frame. */
  | 'SWAP'

export interface RollEvent {
  kind: RollEventKind
  atMs: number
}

// ---------------------------------------------------------------------------
// The five instants
// ---------------------------------------------------------------------------

/**
 * When this tab pre-warms the successor of the market anchored at `anchorMs`.
 *
 * Note where this sits relative to the strike: N+1's strike window opens at
 * `T+50`, ten seconds *after* the earliest pre-warm, and closes at `T+60`. The
 * pre-warm is therefore subscribing a market whose strike does not exist yet —
 * which is fine and is the whole trick of §6.4: a market in `SCHEDULED` or
 * `FIXING` has a real, empty, subscribable book.
 */
export function prewarmAtMs(anchorMs: number, jitter: RollJitter): number {
  return anchorMs + PREWARM_BASE_MS + jitter.prewarmMs
}

/** `T+74+U(0,4)` for the market that expired at `expiryMs`. */
export function teardownAtMs(expiryMs: number, timing: RollTiming, jitter: RollJitter): number {
  return expiryMs + timing.resolveEtaOffsetMs + TEARDOWN_GRACE_MS + jitter.teardownMs
}

/** `T+90+U(0,6)` for the market that expired at `expiryMs`. */
export function retireAtMs(expiryMs: number, jitter: RollJitter): number {
  return expiryMs + RETIRE_AFTER_EXPIRY_MS + jitter.retireMs
}

/**
 * When the watchdog fires for a market whose `resolve_eta_ms` is `resolveEtaMs`.
 *
 * **Read `resolve_eta_ms` off the market payload.** It is published per market
 * and a server that slips it slips this with it; deriving `expiry + 13_000`
 * here would make the watchdog fire early on exactly the market that is late,
 * which is the only market it exists for.
 */
export function watchdogAtMs(resolveEtaMs: number, jitter: RollJitter): number {
  return resolveEtaMs + WATCHDOG_LAG_MS + jitter.watchdogMs
}

/**
 * Whether the watchdog, woken for a market, has anything to ask (§6.5).
 *
 * It stands down on a final answer — `RESOLVED`, `SETTLED`, `VOIDED`,
 * `REFUNDED`, `CANCELLED` — and on a market the tab never held, since a
 * reconcile of one it never watched is a request with no question behind it.
 * **Every other state is a tab that never heard the market end, `OPEN`
 * included.** A market resolves the instant its print lands (§F94 Part A), so
 * the bell's `EXPIRED` and the final state can share one frame, and a tab that
 * missed that frame still holds the market `OPEN` past its `resolve_eta_ms`.
 * This used to list the in-between states instead, and left that tab with no
 * reconcile at all (2026-09-30).
 */
export function watchdogReconciles(state: unknown): boolean {
  if (typeof state !== 'string' || !(MARKET_STATES as readonly string[]).includes(state)) return false
  return phaseOf(state as MarketState) !== 'TERMINAL'
}

/**
 * Which markets a tab holds **beyond the focused one**, at an instant.
 *
 * Declarative rather than accumulated, and that is the whole reason the roll's
 * five instants are a *schedule* rather than a state machine: each hold is a
 * comparison between the corrected clock and one of the instants above, so the
 * answer is a pure function of `(anchor, now)`. A cold load at `T+45`, an asset
 * switch at `T+50` and the pre-warm firing on time all produce the same set, and
 * none of them needs a catch-up path.
 *
 * `anchorS` is the **currently trading** market's anchor, so `anchorS × 1000` is
 * simultaneously the predecessor's `expiry_at_ms` — which is why the teardown
 * and retire instants are measured from it.
 *
 * Anchors, not market ids: this file knows nothing about series, assets or
 * channel names.
 */
export interface RollHolds {
  /** Anchors whose depth channels are held: the pre-warmed successor from
   *  `T+42+U(0,8)`, and the predecessor until `T+74`. */
  depth: number[]
  /** Anchors whose `market.{id}` is held beyond the base set: the
   *  successor-of-the-successor from the pre-warm, and the predecessor until
   *  `T+90`. Both assets, because the base set carries both. */
  markets: number[]
}

export function rollHolds(
  anchorS: number,
  periodS: number,
  nowMs: number,
  timing: RollTiming,
  jitter: RollJitter,
): RollHolds {
  const anchorMs = anchorS * 1000
  const depth: number[] = []
  const markets: number[] = []

  // The pre-warm carries **everything the next cycle's desired set will name**
  // — not merely the successor's book. Subscribing the rest at the boundary
  // instead would be one control frame at the one instant §6.0 Fact 2 forbids
  // any, and 3,000 tabs send it inside one clock error of each other.
  if (nowMs >= prewarmAtMs(anchorMs, jitter)) {
    depth.push(anchorS + periodS)
    markets.push(anchorS + 2 * periodS)
  }

  const previousS = anchorS - periodS
  if (previousS > 0) {
    if (nowMs < retireAtMs(anchorMs, jitter)) markets.push(previousS)
    if (nowMs < teardownAtMs(anchorMs, timing, jitter)) depth.push(previousS)
  }
  return { depth, markets }
}

/**
 * Every instant of one cycle, in time order, for the market anchored at
 * `anchorMs`.
 *
 * The first three concern the market that expired *at* `anchorMs` — the
 * predecessor, which is resolving behind the one now trading — and the last two
 * concern the successor. That is what "two markets on one screen" looks like as
 * a schedule.
 *
 * `resolveEtaMs` is the predecessor's, read from its payload; `null` when the
 * client has no object for it, in which case the watchdog is not scheduled at
 * all. A watchdog fired against a market the tab knows nothing about would be
 * an HTTP request with no question behind it.
 */
export function rollSchedule(
  anchorMs: number,
  timing: RollTiming,
  jitter: RollJitter,
  resolveEtaMs: number | null,
): RollEvent[] {
  const events: RollEvent[] = [
    { kind: 'TEARDOWN', atMs: teardownAtMs(anchorMs, timing, jitter) },
    { kind: 'RETIRE', atMs: retireAtMs(anchorMs, jitter) },
    { kind: 'PREWARM', atMs: prewarmAtMs(anchorMs, jitter) },
    { kind: 'SWAP', atMs: anchorMs + timing.periodMs },
  ]
  if (resolveEtaMs !== null) {
    events.push({ kind: 'WATCHDOG', atMs: watchdogAtMs(resolveEtaMs, jitter) })
  }
  return events.sort((a, b) => a.atMs - b.atMs)
}
